// ── 로그 관리 — 데스크톱 테이블 컴포넌트 ─────────────────────────────────────
import React from 'react';
import AdminColHeader from '../../../components/AdminColHeader';
import { LOG_HEADERS } from './constants';
import { statusColor, methodColor, fmtDatetime, shortPath, formatBodyRows } from './formatters';
import { resolveActionDescription } from './resolveActionDescription';
import { resolveFeatureLabel } from './formatters';
import {
  tableWrapStyle, tblStyle, thStyle, trStyle, tdStyle,
  featureBadgeStyle, methodBadgeStyle, statusBadgeStyle,
  expandGridStyle, expandLabelStyle, actionLabelStyle, actionDetailStyle,
  bodyTableStyle, bodyKeyTdStyle, bodyValTdStyle, jsonPreStyle,
} from './styles';

/**
 * 로그 테이블 (데스크톱 전용) — 행 클릭으로 request_body 상세 확장
 * @param {object[]} logs        — 현재 페이지 로그 목록
 * @param {number|null} expandedId — 현재 확장된 로그 ID (null이면 없음)
 * @param {Function} onToggleExpand — 확장 토글 콜백 (logId) => void
 */
export function LogTable({ logs, expandedId, onToggleExpand }) {
  return (
    <div style={tableWrapStyle}>
      <table style={tblStyle}>
        <thead>
          <tr>
            {LOG_HEADERS.map(({ label, db }) => (
              <th key={label} style={thStyle}>
                <AdminColHeader label={label} db={db} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {/* 빈 상태 */}
          {logs.length === 0 && (
            <tr>
              <td colSpan={8} style={{ ...tdStyle, textAlign: 'center', color: 'var(--text-muted)', padding: 32 }}>
                로그가 없습니다.
              </td>
            </tr>
          )}

          {logs.map((log, idx) => {
            const sc          = statusColor(log.status_code);
            const mc          = methodColor(log.method);
            const featureInfo = resolveFeatureLabel(log.path);
            // 한국어 동작 설명 (path + method + request_body 분석)
            const actionDesc  = resolveActionDescription(log.path, log.method, log.request_body);
            // request_body → 한국어 라벨 키-값 배열
            const bodyRows    = formatBodyRows(log.request_body);
            const isExpanded  = expandedId === log.id;

            return (
              <React.Fragment key={log.id}>
                {/* 기본 행 — 클릭하면 상세 확장 */}
                <tr
                  style={{ ...trStyle(idx), cursor: 'pointer' }}
                  onClick={() => onToggleExpand(log.id)}
                  title="클릭하면 상세 내용을 확인합니다"
                >
                  <td style={{ ...tdStyle, color: 'var(--text-muted)', fontSize: 11 }}>{log.id}</td>
                  <td style={{ ...tdStyle, whiteSpace: 'nowrap', fontSize: 11 }}>{fmtDatetime(log.created_at)}</td>
                  {/* 직원명 + 아이디 */}
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 700, fontSize: 12, color: 'var(--text-sub)' }}>
                      {log.actor_name || <span style={{ color: 'var(--text-muted)' }}>-</span>}
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{log.actor_login_id || '-'}</div>
                  </td>
                  {/* 기능 배지 + 한국어 동작 설명 */}
                  <td style={{ ...tdStyle, minWidth: 120 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                      {featureInfo
                        ? <span style={{ ...featureBadgeStyle, color: featureInfo.color, background: featureInfo.color + '18', border: `1px solid ${featureInfo.color}30` }}>{featureInfo.label}</span>
                        : <span style={{ color: 'var(--border)', fontSize: 10 }}>—</span>
                      }
                    </div>
                    {actionDesc && (
                      <div style={{ marginTop: 3 }}>
                        <span style={actionLabelStyle}>{actionDesc.label}</span>
                        {actionDesc.detail && <span style={actionDetailStyle}> {actionDesc.detail}</span>}
                      </div>
                    )}
                  </td>
                  <td style={tdStyle}>
                    <span style={{ ...methodBadgeStyle, background: mc.bg, color: mc.text }}>{log.method}</span>
                  </td>
                  <td style={{ ...tdStyle, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <span title={log.path} style={{ fontFamily: 'monospace', fontSize: 11 }}>{shortPath(log.path)}</span>
                  </td>
                  <td style={tdStyle}>
                    <span style={{ ...statusBadgeStyle, background: sc.bg, color: sc.text }}>{log.status_code}</span>
                  </td>
                  <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 11, color: 'var(--text-muted)' }}>{log.ip_address || '-'}</td>
                </tr>

                {/* 확장 상세 행 */}
                {isExpanded && (
                  <tr style={{ background: 'var(--bg-subtle)' }}>
                    <td colSpan={8} style={{ padding: '10px 16px' }}>
                      {/* 한국어 필드 표 */}
                      {bodyRows.length > 0 && (
                        <div style={{ marginBottom: 10 }}>
                          <div style={{ ...expandLabelStyle, marginBottom: 6 }}>요청 내용 (한국어)</div>
                          <table style={bodyTableStyle}>
                            <tbody>
                              {bodyRows.map(({ key, label, value }) => (
                                <tr key={key}>
                                  <td style={bodyKeyTdStyle}>{label}</td>
                                  <td style={bodyValTdStyle}>{value}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                      {/* 전체 PATH + User-Agent */}
                      <div style={expandGridStyle}>
                        <div>
                          <span style={expandLabelStyle}>전체 PATH</span>
                          <span style={{ fontFamily: 'monospace', color: 'var(--text-sub)', fontSize: 12 }}>{log.path}</span>
                        </div>
                        {log.user_agent && (
                          <div>
                            <span style={expandLabelStyle}>User-Agent</span>
                            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{log.user_agent}</span>
                          </div>
                        )}
                      </div>
                      {/* 날것 JSON */}
                      {log.request_body
                        ? (
                          <div style={{ marginTop: 8 }}>
                            <div style={{ ...expandLabelStyle, marginBottom: 4 }}>Request Body (JSON)</div>
                            <pre style={jsonPreStyle}>{JSON.stringify(log.request_body, null, 2)}</pre>
                          </div>
                        )
                        : <div style={{ fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic', marginTop: 4 }}>요청 바디 없음</div>
                      }
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
